import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Receipt, Loader2, Eye, Printer, IndianRupee, Filter } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudentReceipts() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [receipts, setReceipts] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const token = localStorage.getItem('studentToken');
    if (!token) {
      navigate('/student/login');
      return;
    }
    fetchReceipts();
  }, [navigate]);

  const fetchReceipts = async () => {
    const token = localStorage.getItem('studentToken');
    
    try {
      const response = await fetch(`${API_URL}/api/student-portal/receipts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReceipts(data.receipts || []);
      }
    } catch (error) {
      console.error('Error fetching receipts:', error);
      toast.error('Failed to load receipts');
    } finally {
      setIsLoading(false);
    }
  };

  const getFeeTypeLabel = (type) => {
    const labels = {
      'course_fee': 'Course Fee',
      'booking': 'Seat Booking',
      'admission': 'Admission Fee',
      'hostel': 'Hostel Fee',
      'transport': 'Transport Fee',
      'library_fine': 'Library Fine',
      'attendance_fine': 'Attendance Fine',
      'other_fee': 'Other Fee',
      'hostel_fine': 'Hostel Fine',
      'hostel_damage': 'Hostel Damage',
      'library_damage': 'Library Damage',
      'transport_damage': 'Transport Damage',
      'other': 'Other Fee'
    };
    return labels[type] || type || 'Fee Payment';
  };

  const getFeeTypeBadgeColor = (type) => {
    const colors = {
      'course_fee': 'bg-blue-100 text-blue-700',
      'booking': 'bg-green-100 text-green-700',
      'admission': 'bg-indigo-100 text-indigo-700',
      'hostel': 'bg-purple-100 text-purple-700',
      'transport': 'bg-orange-100 text-orange-700',
      'library_fine': 'bg-red-100 text-red-700',
      'attendance_fine': 'bg-yellow-100 text-yellow-700',
      'other_fee': 'bg-cyan-100 text-cyan-700',
      'hostel_fine': 'bg-pink-100 text-pink-700',
      'hostel_damage': 'bg-rose-100 text-rose-700',
      'library_damage': 'bg-red-100 text-red-800',
      'transport_damage': 'bg-amber-100 text-amber-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const filteredReceipts = filter === 'all' 
    ? receipts 
    : receipts.filter(r => {
        const code = r.fee_type_code || '';
        const feeType = (r.fee_type || '').toLowerCase();
        
        // Handle "other" filter - match both "other_fee" and "other"
        if (filter === 'other') {
          return code === 'other_fee' || code === 'other' || feeType.includes('other');
        }
        
        // Handle "damage" filter - match all damage types
        if (filter === 'damage') {
          return code.includes('damage') || feeType.includes('damage');
        }
        
        // Handle "hostel" filter - include hostel fines too
        if (filter === 'hostel') {
          return code === 'hostel' || code === 'hostel_fine' || code === 'hostel_damage' || feeType.includes('hostel');
        }
        
        // Handle "library" filter
        if (filter === 'library') {
          return code === 'library_fine' || code === 'library_damage' || feeType.includes('library');
        }
        
        // Handle "transport" filter
        if (filter === 'transport') {
          return code === 'transport' || code === 'transport_fee' || code === 'transport_damage' || feeType.includes('transport');
        }
        
        // Default: match fee_type_code or fee_type contains filter
        return code === filter || feeType.includes(filter);
      });

  const totalAmount = filteredReceipts.reduce((sum, r) => sum + (r.amount || 0), 0);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/student/dashboard')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-xl font-semibold">All Receipts</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Summary Card */}
        <Card className="mb-6 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100">Total Payments</p>
                <p className="text-3xl font-bold">₹{totalAmount.toLocaleString('en-IN')}</p>
              </div>
              <div className="text-right">
                <p className="text-indigo-100">Total Receipts</p>
                <p className="text-3xl font-bold">{filteredReceipts.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filter */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          <Button 
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
          >
            All
          </Button>
          <Button 
            variant={filter === 'booking' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('booking')}
          >
            Booking
          </Button>
          <Button 
            variant={filter === 'admission' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('admission')}
          >
            Admission
          </Button>
          <Button 
            variant={filter === 'course' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('course')}
          >
            Course Fee
          </Button>
          <Button 
            variant={filter === 'other' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('other')}
          >
            Other Fee
          </Button>
          <Button 
            variant={filter === 'hostel' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('hostel')}
          >
            Hostel
          </Button>
          <Button 
            variant={filter === 'transport' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('transport')}
          >
            Transport
          </Button>
          <Button 
            variant={filter === 'library' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('library')}
          >
            Library
          </Button>
          <Button 
            variant={filter === 'damage' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('damage')}
          >
            Damage Fees
          </Button>
        </div>

        {/* Receipts List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" /> Payment Receipts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredReceipts.length === 0 ? (
              <div className="text-center py-12">
                <Receipt className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No receipts found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredReceipts.map((receipt, idx) => (
                  <div 
                    key={idx} 
                    className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-semibold text-indigo-600">
                            {receipt.receipt_no}
                          </span>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${getFeeTypeBadgeColor(receipt.fee_type_code)}`}>
                            {receipt.fee_type || getFeeTypeLabel(receipt.fee_type_code)}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span>{receipt.payment_date}</span>
                          <span>{receipt.payment_mode}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-lg font-bold text-green-600">
                            ₹{receipt.amount?.toLocaleString('en-IN')}
                          </p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigate(`/student/receipt/${receipt.receipt_no}`)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
